export const toothColors = {
  healthy: '#1E1ED6',
  pathological: '#e83232',
  neutral: '#1a1a1a',
};

export const toothTypes = {
  incisor: 'incisor',
  canine: 'canine',
  premolar: 'premolar',
  molar: 'molar',
};

const getToothType = (num) => {
  const toothPosition = num % 10;
  if (toothPosition <= 2) return toothTypes.incisor;
  if (toothPosition === 3) return toothTypes.canine;
  if (toothPosition <= 5) return toothTypes.premolar;
  return toothTypes.molar;
};

export const getOclusalSurfaces = (toothNumber) => {
  const lastDigit = toothNumber % 10;
  const quadrant = Math.floor(toothNumber / 10);
  const isPermanent = quadrant <= 4;

  if (lastDigit <= 3) return [];
  let columns = 1, rows = 1;
  if (lastDigit === 8) { columns = 3; rows = 2; }
  else if (lastDigit >= 6 || (!isPermanent && lastDigit >= 4)) { columns = 2; rows = 2; }
  else if (isPermanent && lastDigit <= 5) { rows = 2; }

  const surfaces = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      surfaces.push(`O_${row}_${column}`);
    }
  }
  return surfaces;
};

export const toothSizes = {
  width: 30,
  height: 40,
  crownSize: 35,
  rootHeight: 30,
  boxHeight: 20,
};

export const toothQuadrants = {
  permanentSuperior: {
    right: [18, 17, 16, 15, 14, 13, 12, 11].map(num => ({ num, type: getToothType(num) })),
    left: [21, 22, 23, 24, 25, 26, 27, 28].map(num => ({ num, type: getToothType(num) })),
  },
  deciduousSuperior: {
    right: [55, 54, 53, 52, 51].map(num => ({ num, type: getToothType(num) })),
    left: [61, 62, 63, 64, 65].map(num => ({ num, type: getToothType(num) })),
  },
  deciduousInferior: {
    right: [85, 84, 83, 82, 81].map(num => ({ num, type: getToothType(num) })),
    left: [71, 72, 73, 74, 75].map(num => ({ num, type: getToothType(num) })),
  },
  permanentInferior: {
    right: [48, 47, 46, 45, 44, 43, 42, 41].map(num => ({ num, type: getToothType(num) })),
    left: [31, 32, 33, 34, 35, 36, 37, 38].map(num => ({ num, type: getToothType(num) })),
  },
};

export const toothOrderRows = [
  [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
  [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
  [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
];

export const permanentTeeth = toothOrderRows.flatMap(row =>
  row.filter(t => Math.floor(t / 10) <= 4)
);
export const deciduousTeeth = toothOrderRows.flatMap(row =>
  row.filter(t => Math.floor(t / 10) >= 5)
);
export const thirdMolars = [18, 28, 38, 48];

export const permanent28 = permanentTeeth.filter(t => !thirdMolars.includes(t));

export const isUpperTooth = (num) => {
  const quad = Math.floor(parseInt(num, 10) / 10);
  return [1, 2, 5, 6].includes(quad);
};

export const isLowerTooth = (num) => {
  const quad = Math.floor(parseInt(num, 10) / 10);
  return [3, 4, 7, 8].includes(quad);
};

export const toothSurfaces = {
  mesial: {
    key: 'mesial',
    label: 'Mesial',
    svgZone: 'M',
  },
  distal: {
    key: 'distal',
    label: 'Distal',
    svgZone: 'D',
  },
  vestibular: {
    key: 'vestibular',
    label: 'Vestibular',
    svgZone: 'V',
  },
  palatine: {
    key: 'palatine',
    label: 'Palatina / Lingual',
    svgZone: 'L',
  },
  oclusal: {
    key: 'oclusal',
    label: 'Oclusal',
    svgZone: 'O',
    applicableTo: [toothTypes.molar, toothTypes.premolar],
  },
  incisal: {
    key: 'incisal',
    label: 'Incisal',
    svgZone: 'I',
    applicableTo: [toothTypes.incisor, toothTypes.canine],
    clickable: false,
  },
};

export const conditionSymbols = {

  fixedOrthodonticAppliance: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <rect x="2" y="6" width="8" height="8" stroke={color} strokeWidth="1.2" fill="none" />
      <line x1="3" y1="10" x2="9" y2="10" stroke={color} strokeWidth="1" />
      <line x1="6" y1="7" x2="6" y2="13" stroke={color} strokeWidth="1" />
      <line x1="10" y1="10" x2="20" y2="10" stroke={color} strokeWidth="1.2" />
      <rect x="20" y="6" width="8" height="8" stroke={color} strokeWidth="1.2" fill="none" />
      <line x1="21" y1="10" x2="27" y2="10" stroke={color} strokeWidth="1" />
      <line x1="24" y1="7" x2="24" y2="13" stroke={color} strokeWidth="1" />
    </svg>
  ),
  removableOrthodonticAppliance: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <path d="M2,14 L8,6 L14,14 L20,6 L26,14" stroke={color} strokeWidth="1.8" fill="none" />
    </svg>
  ),
  caries: (color = '#e83232', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="2" width="16" height="16" rx="2" fill={color} opacity="0.85" />
    </svg>
  ),
  occlusalWear: (color = '#e83232', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <path d="M2 16 L14 4 L28 4" stroke={color} strokeWidth="2" fill="none" />
      <line x1="2" y1="16" x2="28" y2="16" stroke={color} strokeWidth="1.5" strokeDasharray="2 2" />
    </svg>
  ),
  missingTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="2" x2="18" y2="18" stroke={color} strokeWidth="2" />
      <line x1="18" y1="2" x2="2" y2="18" stroke={color} strokeWidth="2" />
    </svg>
  ),
  edentulousArch: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <line x1="1" y1="10" x2="29" y2="10" stroke={color} strokeWidth="2" />
    </svg>
  ),
  fracture: (color = '#e83232', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="18" x2="18" y2="2" stroke={color} strokeWidth="2.5" />
    </svg>
  ),
  pathologicalMobility: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <text x="2" y="15" fontSize="13" fontWeight="bold" fill={color} fontFamily="monospace">M</text>
    </svg>
  ),
  radicalRemnant: (color = '#e83232', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="1" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">RR</text>
    </svg>
  ),
  definiteCrown: (color = toothColors.healthy, size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">CD</text>
    </svg>
  ),
  temporaryCrown: (color = '#e83232', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">CT</text>
    </svg>
  ),
  fixedProsthesis: (color = toothColors.healthy, size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">PF</text>
    </svg>
  ),
  removableProsthesis: (color = toothColors.healthy, size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">PR</text>
    </svg>
  ),
  totalProsthesis: (color = toothColors.healthy, size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">PT</text>
    </svg>
  ),
  diastema: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M8,3 Q3,10 8,17" stroke={color} strokeWidth="1.8" fill="none" />
      <path d="M12,3 Q17,10 12,17" stroke={color} strokeWidth="1.8" fill="none" />
    </svg>
  ),
  discoloredTooth: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">DIS</text>
    </svg>
  ),
  ectopicTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <text x="4" y="15" fontSize="13" fontWeight="bold" fill={color} fontFamily="monospace">E</text>
    </svg>
  ),
  pegTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <polygon points="10,2 18,18 2,18" fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  ),
  extrudedTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="16" x2="10" y2="4" stroke={color} strokeWidth="2" />
      <polygon points="10,1 6,7 14,7" fill={color} />
    </svg>
  ),
  intrudedTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="4" x2="10" y2="16" stroke={color} strokeWidth="2" />
      <polygon points="10,19 6,13 14,13" fill={color} />
    </svg>
  ),
  gemination: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke={color} strokeWidth="1.8" fill="none" />
    </svg>
  ),
  fusion: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <ellipse cx="10" cy="10" rx="7" ry="5" stroke={color} strokeWidth="1.5" />
      <ellipse cx="20" cy="10" rx="7" ry="5" stroke={color} strokeWidth="1.5" />
    </svg>
  ),
  gyroversion: (color = '#000', size = 20) => (
    <svg width={size * 1.4} height={size} viewBox="0 0 28 16" fill="none">
      <path d="M 3,10 Q 14,16 25,10" stroke={color} strokeWidth="1.6" fill="none" />
      <line x1="25" y1="10" x2="21" y2="7" stroke={color} strokeWidth="1.6" />
      <line x1="25" y1="10" x2="22" y2="14" stroke={color} strokeWidth="1.6" />
    </svg>
  ),
  impaction: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <text x="6" y="15" fontSize="13" fontWeight="bold" fill={color} fontFamily="monospace">I</text>
    </svg>
  ),
  macrodontia: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="10" fontWeight="bold" fill={color} fontFamily="monospace">MAC</text>
    </svg>
  ),
  microdontia: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="10" fontWeight="bold" fill={color} fontFamily="monospace">MIC</text>
    </svg>
  ),
  migration: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="10" x2="16" y2="10" stroke={color} strokeWidth="2" />
      <polygon points="19,10 13,6 13,14" fill={color} />
    </svg>
  ),
  semiImpaction: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="11" fontWeight="bold" fill={color} fontFamily="monospace">SI</text>
    </svg>
  ),
  supernumerary: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke={color} strokeWidth="1.6" fill="none" />
      <text x="10" y="14" textAnchor="middle" fontSize="10" fontWeight="bold" fill={color} fontFamily="monospace">S</text>
    </svg>
  ),
  transposition: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <path d="M2,16 Q13,-2 24,16" stroke={color} strokeWidth="1.6" fill="none" />
      <path d="M6,16 Q17,-2 28,16" stroke={color} strokeWidth="1.6" fill="none" />
      <path d="M21,12.5 L24,16 L19,15.5" stroke={color} strokeWidth="1.5" />
      <path d="M9,12.5 L6,16 L11,15.5" stroke={color} strokeWidth="1.5" />
    </svg>
  ),
  eruptingTooth: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="16" x2="10" y2="6" stroke={color} strokeWidth="2" />
      <polygon points="10,3 6,9 14,9" fill={color} />
      <path d="M5 16 Q10 12 15 16" stroke={color} strokeWidth="1.5" fill="none" strokeDasharray="2 2" />
    </svg>
  ),
  extraction: (color = '#e83232', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="2" x2="18" y2="18" stroke={color} strokeWidth="2.5" />
      <line x1="18" y1="2" x2="2" y2="18" stroke={color} strokeWidth="2.5" />
    </svg>
  ),
  implant: (color = '#000', size = 20) => (
    <svg width={size * 1.5} height={size} viewBox="0 0 30 20" fill="none">
      <text x="0" y="15" fontSize="10" fontWeight="bold" fill={color} fontFamily="monospace">IMP</text>
    </svg>
  ),
  restoration: (color = toothColors.healthy, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="2" width="16" height="16" rx="2" fill={color} />
    </svg>
  ),
  temporaryRestoration: (color = '#e83232', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="2" width="16" height="16" rx="2" fill="none" stroke={color} strokeWidth="2" />
    </svg>
  ),
  sealant: (color = toothColors.healthy, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="2" width="16" height="16" rx="2" fill="none" stroke={color} strokeWidth="2" />
    </svg>
  ),
  pulpTreatmentCompleted: (color = toothColors.healthy, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="2" x2="10" y2="18" stroke={color} strokeWidth="2.5" />
    </svg>
  ),
  pulpTreatmentPending: (color = '#e83232', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="2" x2="10" y2="18" stroke={color} strokeWidth="2.5" />
    </svg>
  ),
  postAndCore: (color = '#000', size = 20) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <text x="4" y="15" fontSize="13" fontWeight="bold" fill={color} fontFamily="monospace">T</text>
    </svg>
  ),
};

//

export const conditionCategories = [
  {
    key: 'orthodontics',
    label: 'Ortodoncia',
    conditions: [
      {
        code: 'APARATO_ORTODONTICO_FIJO',
        label: 'Aparato Ortodóntico Fijo',
        symbolKey: 'fixedOrthodonticAppliance',
        colorMode: 'user',
        renderType: 'ortho_fixed',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
        specs: 'Tipo de aparatología encontrada',
      },
      {
        code: 'APARATO_ORTODONTICO_REMOVIBLE',
        label: 'Aparato Ortodóntico Removible',
        symbolKey: 'removableOrthodonticAppliance',
        colorMode: 'user',
        renderType: 'ortho_removable',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
        specs: 'Tipo de aparatología encontrada',
      },
    ],
  },

  {
    key: 'pathology',
    label: 'Patología',
    conditions: [
      {
        code: 'CARIES',
        label: 'Caries Dental',
        symbolKey: 'caries',
        colorMode: 'pathological',   
        renderType: 'surface_fill',
        appliesToWholeTooth: false,
        showInGrid: true,
        showOptions: false,         
        options: [
          { code: 'MB', label: 'Mancha Blanca' },
          { code: 'CE', label: 'Caries del Esmalte' },
          { code: 'CD', label: 'Caries de Dentina' },
          { code: 'CDP', label: 'Caries a nivel de la Pulpa' },
        ],
      },
      {
        code: 'DESGASTE',
        label: 'Desgaste Oclusal / Incisal',
        symbolKey: null,
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,  
        showInGrid: true,
        gridCode: 'DES',
      },
      {
        code: 'AUSENTE',
        label: 'Diente Ausente',
        symbolKey: 'missingTooth',
        colorMode: 'healthy',        
        renderType: 'aspa',
        appliesToWholeTooth: true,
        showInGrid: true,
        options: [
          { code: 'DNE', label: 'Diente No Erupcionado' },
          { code: 'DEX', label: 'Ausente por Extracción' },
          { code: 'DAO', label: 'Ausente por Otras Razones' },
        ],
      },
      {
        code: 'REMANENTE_RADICULAR',
        label: 'Remanente Radicular',
        symbolKey: 'radicalRemnant',
        colorMode: 'pathological',  
        renderType: 'root_text',
        appliesToWholeTooth: true,
        showInGrid: false,
        specs: 'Tipo de material',
      },
      {
        code: 'EDENTULO_TOTAL',
        label: 'Edéntulo Total',
        symbolKey: 'edentulousArch',
        colorMode: 'healthy',        
        renderType: 'line_arch',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
        specs: 'Indicar si afecta maxilar superior, inferior o ambos',
      },
      {
        code: 'FRACTURA',
        label: 'Fractura',
        symbolKey: 'fracture',
        colorMode: 'pathological',
        renderType: 'line_fracture',
        appliesToWholeTooth: true,
        showInGrid: false,
        options: [
          {
            code: 'C_45',
            label: 'Corona ╱',
            icon: (color) => (
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <line x1="3" y1="14" x2="17" y2="2" stroke={color} strokeWidth="2" />
              </svg>
            ),
          },
          {
            code: 'C_90',
            label: 'Corona ─',
            icon: (color) => (
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <line x1="2" y1="8" x2="18" y2="8" stroke={color} strokeWidth="2" />
              </svg>
            ),
          },
          {
            code: 'C_135',
            label: 'Corona ╲',
            icon: (color) => (
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <line x1="3" y1="2" x2="17" y2="14" stroke={color} strokeWidth="2" />
              </svg>
            ),
          },
        ],
      },
      {
        code: 'MOVILIDAD',
        label: 'Movilidad',
        symbolKey: 'pathologicalMobility',
        colorMode: 'healthy',   
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'M',               
        options: [
          { code: 'M1', label: 'Grado 1' },
          { code: 'M2', label: 'Grado 2' },
          { code: 'M3', label: 'Grado 3' },
        ],
        specs: 'El grado se selecciona antes de hacer clic en la pieza',
      },
      {
        code: 'DEFECTO_DESARROLLO_ESMALTE',
        label: 'Defectos de Desarrollo del Esmalte',
        symbolKey: null,
        colorMode: 'pathological',   
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'DDE',
        options: [
          { code: 'O', label: 'Opacidades del Esmalte' },
          { code: 'PE', label: 'Pigmentación del Esmalte' },
          { code: 'FL', label: 'Fluorosis' },
        ],
        specs: 'Fluorosis: detallar en especificaciones con la clasificación utilizada',
      },

    ],
  },
  {
    key: 'prosthetics',
    label: 'Prótesis',
    conditions: [
      {
        code: 'CORONA_DEFINITIVA',
        label: 'Corona Definitiva',
        symbolKey: 'definiteCrown',
        colorMode: 'healthy',
        renderType: 'crown_outline',
        appliesToWholeTooth: true,
        showInGrid: true,
        options: [
          { code: 'CC', label: 'Corona Completa (metálica)' },
          { code: 'CF', label: 'Corona Fenestrada' },
          { code: 'CMC', label: 'Corona Metal-Cerámica' },
          { code: '3/4', label: 'Corona 3/4 (parcial metálica)' },
          { code: '4/5', label: 'Corona 4/5 (parcial metálica)' },
          { code: '7/8', label: 'Corona 7/8 (parcial metálica)' },
          { code: 'CV', label: 'Corona Veneer' },
          { code: 'CJ', label: 'Corona Jacket' },
        ],
        specs: 'Color del metal: dorada o plateada',
      },
      {
        code: 'CORONA_TEMPORAL',
        label: 'Corona Temporal',
        symbolKey: 'temporaryCrown',
        colorMode: 'pathological',
        renderType: 'crown_outline',
        appliesToWholeTooth: true,
        showInGrid: false,          
      },
      {
        code: 'PROTESIS_FIJA',
        label: 'Prótesis Fija',
        symbolKey: 'fixedProsthesis',
        colorMode: 'user',
        renderType: 'bracket',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
      },
      {
        code: 'PROTESIS_REMOVIBLE',
        label: 'Prótesis Removible',
        symbolKey: 'removableProsthesis',
        colorMode: 'user',
        renderType: 'double_line_apex',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
      },
      {
        code: 'PROTESIS_TOTAL',
        label: 'Prótesis Total',
        symbolKey: 'totalProsthesis',
        colorMode: 'user',
        renderType: 'double_line_crown',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
        specs: 'Tipo de material',
      },
      {
        code: 'IMPLANTE',
        label: 'Implante Dental',
        symbolKey: 'implant',
        colorMode: 'user',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        fixedZone: 'root',
        showInGrid: true,
        gridCode: 'IMP',
      },
    ],
  },

  {
    key: 'anomaly',
    label: 'Anomalía',
    conditions: [
      {
        code: 'DISCROMICO',
        label: 'Diente Discrómico',
        symbolKey: 'discoloredTooth',
        colorMode: 'healthy',        
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'DIS',
      },
      {
        code: 'ECTOPICO',
        label: 'Diente Ectópico',
        symbolKey: 'ectopicTooth',
        colorMode: 'healthy',        
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'E',
      },
      {
        code: 'CLAVIJA',
        label: 'Diente en Clavija',
        symbolKey: 'pegTooth',
        colorMode: 'healthy',        
        renderType: 'triangle_root',
        appliesToWholeTooth: true,
        showInGrid: false,
      },
      {
        code: 'EXTRUSION',
        label: 'Diente Extruido',
        symbolKey: 'extrudedTooth',
        colorMode: 'healthy',        
        renderType: 'arrow_occlusal',
        appliesToWholeTooth: true,
        showInGrid: false,
      },
      {
        code: 'INTRUSION',
        label: 'Diente Intruido',
        symbolKey: 'intrudedTooth',
        colorMode: 'healthy',        
        renderType: 'arrow_apex',
        appliesToWholeTooth: true,
        showInGrid: false,
      },
      {
        code: 'FUSION',
        label: 'Fusión',
        symbolKey: 'fusion',
        colorMode: 'healthy',        
        renderType: 'intersecting_circles',
        appliesToWholeTooth: true,
        multiTooth: true,
        requiresAdjacency: true,
        showInGrid: false,
      },
      {
        code: 'GEMINACION',
        label: 'Geminación',
        symbolKey: 'gemination',
        colorMode: 'healthy',       
        renderType: 'circleNumber',
        appliesToWholeTooth: true,
        showInGrid: false,
      },
      {
        code: 'GIROVERSION',
        label: 'Giroversión',
        symbolKey: 'gyroversion',
        colorMode: 'healthy',        
        renderType: 'curved_arrow',
        appliesToWholeTooth: true,
        showInGrid: false,
        options: [
          {
            code: 'HOR', label: '⟶',
            icon: (color) => (
              <svg width="32" height="14" viewBox="0 0 32 14" fill="none">
                <path d="M 3,8 Q 16,2 29,8" stroke={color} strokeWidth="1.6" fill="none" />
                <line x1="29" y1="8" x2="26" y2="4" stroke={color} strokeWidth="1.6" />
                <line x1="29" y1="8" x2="24" y2="9" stroke={color} strokeWidth="1.6" />
              </svg>
            ),
          },
          {
            code: 'ANTIHOR', label: '⟵',
            icon: (color) => (
              <svg width="32" height="14" viewBox="0 0 32 14" fill="none">
                <path d="M 3,8 Q 16,2 29,8" stroke={color} strokeWidth="1.6" fill="none" />
                <line x1="3" y1="8" x2="6" y2="4" stroke={color} strokeWidth="1.6" />
                <line x1="3" y1="8" x2="8" y2="9" stroke={color} strokeWidth="1.6" />
              </svg>
            ),
          },
        ],
      },
      {
        code: 'IMPACTACION',
        label: 'Impactación',
        symbolKey: 'impaction',
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'I',
      },
      {
        code: 'SEMI_IMPACTACION',
        label: 'Semi-Impactación',
        symbolKey: 'semiImpaction',
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'SI',
      },
      {
        code: 'MACRODONCIA',
        label: 'Macrodoncia',
        symbolKey: 'macrodontia',
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'MAC',
      },
      {
        code: 'MICRODONCIA',
        label: 'Microdoncia',
        symbolKey: 'microdontia',
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'MIC',
      },
      {
        code: 'MIGRACION',
        label: 'Migración',
        symbolKey: 'migration',
        colorMode: 'healthy',
        renderType: 'arrow_horizontal',
        appliesToWholeTooth: true,
        showInGrid: false,
        options: [
          { code: 'DER', label: '→' },
          { code: 'IZQ', label: '←' },
        ],
      },
      {
        code: 'SUPERNUMERARIO',
        label: 'Pieza Supernumeraria',
        symbolKey: 'supernumerary',
        colorMode: 'healthy',
        renderType: 'circle_s',
        appliesToWholeTooth: true,
        multiTooth: true,
        showInGrid: false,
      },
      {
        code: 'TRANSPOSICION',
        label: 'Transposición',
        symbolKey: 'transposition',
        colorMode: 'healthy',
        renderType: 'crossed_curved_arrows',
        appliesToWholeTooth: true,
        multiTooth: true,
        requiresAdjacency: true,
        showInGrid: false,
      },
      {
        code: 'DIASTEMA',
        label: 'Diastema',
        symbolKey: 'diastema',
        colorMode: 'healthy',
        renderType: 'brackets_inv',
        appliesToWholeTooth: true,
        multiTooth: true,
        requiresAdjacency: true,
        showInGrid: false,
      },
    ],
  },

  {
    key: 'treatment',
    label: 'Tratamiento',
    conditions: [
      {
        code: 'RESTAURACION',
        label: 'Restauración Definitiva',
        symbolKey: 'restoration',
        colorMode: 'healthy',
        renderType: 'surface_fill',
        appliesToWholeTooth: false,
        showInGrid: true,
        options: [
          { code: 'AM', label: 'Amalgama' },
          { code: 'R', label: 'Resina' },
          { code: 'IV', label: 'Ionómero de Vidrio' },
          { code: 'IM', label: 'Incrustación Metálica' },
          { code: 'IE', label: 'Incrustación Estética' },
          { code: 'C', label: 'Carilla Estética' },
        ],
      },
      {
        code: 'RESTAURACION_TEMPORAL',
        label: 'Restauración Temporal',
        symbolKey: 'temporaryRestoration',
        colorMode: 'pathological',
        renderType: 'surface_outline_red',
        appliesToWholeTooth: false,
        showInGrid: false,
      },
      {
        code: 'SELLANTE',
        label: 'Sellante de Fosas y Fisuras',
        symbolKey: 'sealant',
        colorMode: 'user',
        renderType: 'surface_outline',
        appliesToWholeTooth: false,
        showInGrid: true,
        gridCode: 'S',
      },
      {
        code: 'FISURA_FOSA_PROFUNDA',
        label: 'Fosas y Fisuras Profundas',
        symbolKey: null,
        colorMode: 'healthy',
        renderType: 'text_box',
        appliesToWholeTooth: true,
        showInGrid: true,
        gridCode: 'FFP',
      },
      {
        code: 'TRATAMIENTO_CONDUCTOS',
        label: 'Tratamiento Pulpar',
        symbolKey: 'pulpTreatmentCompleted',
        colorMode: 'healthy',
        renderType: 'vertical_root_line',
        appliesToWholeTooth: true,
        fixedZone: 'root',
        showInGrid: true,
        options: [
          { code: 'TC', label: 'Tratamiento de Conductos' },
          { code: 'PC', label: 'Pulpectomía' },
          { code: 'PP', label: 'Pulpotomía' },
        ],
      },
      {
        code: 'TRATAMIENTO_PULPAR_POR_REALIZAR',
        label: 'Tratamiento Pulpar por Realizar',
        symbolKey: 'pulpTreatmentPending',
        colorMode: 'pathological',
        renderType: 'vertical_root_line',
        appliesToWholeTooth: true,
        fixedZone: 'root',
        showInGrid: false,
        options: [
          { code: 'TC', label: 'Tratamiento de Conductos' },
          { code: 'PC', label: 'Pulpectomía' },
          { code: 'PP', label: 'Pulpotomía' },
        ],
      },
    ],
  },

  {
    key: 'general',
    label: 'Estado General',
    conditions: [
    ],
  },

  {
    key: 'clinicalIndices',
    label: 'Índices Clínicos',
    conditions: [
      {
        code: 'INDICE_GINGIVAL',
        label: 'Código Gingival',
        symbolKey: 'gingivalCode',
        colorMode: 'user',
        renderType: 'text_box',
        appliesToWholeTooth: false,
        showInGrid: false,
        specs: 'Registrar en mesial, distal, vestibular y lingual',
        options: [
          { code: '0', label: '0 – Normal' },
          { code: '1', label: '1 – Inflamación leve' },
          { code: '2', label: '2 – Inflamación moderada' },
          { code: '3', label: '3 – Inflamación severa' },
        ],
      },
    ],
  },
];

export const resolveConditionColor = (condition, userSelectedColor) => {
  if (condition.colorMode === 'healthy') return toothColors.healthy;
  if (condition.colorMode === 'pathological') return toothColors.pathological;
  return userSelectedColor ?? toothColors.healthy;
};

export const findingTypes = conditionCategories.reduce((acc, category) => {
  category.conditions.forEach(condition => {
    const resolvedColor = condition.colorMode === 'healthy'
      ? toothColors.healthy
      : condition.colorMode === 'pathological'
        ? toothColors.pathological
        : null;

    acc[condition.code] = {
      ...condition,
      color: resolvedColor,
      fixedColor: condition.colorMode !== 'user' ? resolvedColor : null,
    };
  });
  return acc;
}, {});

export const resolveColor = (finding) => {
  const cfg = findingTypes[finding.type] || findingTypes[finding.code];
  return cfg?.color || finding.color || toothColors.neutral;
};
export const conditionRenderers = {
  missingTooth: (finding, { crownTop, rootPeak, crownBottom, color }) => {
    const topY = Math.min(crownTop, rootPeak) + 4;
    const bottomY = Math.max(crownBottom, rootPeak) - 4;
    return (
      <g stroke={color} strokeWidth="2.5" pointerEvents="none">
        <line x1="5" y1={topY} x2="35" y2={bottomY} />
        <line x1="35" y1={topY} x2="5" y2={bottomY} />
      </g>
    );
  },
  radicalRemnant: (finding, { rootBase, rootPeak, color }) => {
    const rootMidY = (rootBase + rootPeak) / 2;
    return (
      <text x="20" y={rootMidY + 4} textAnchor="middle" fontSize="14" fontWeight="bold" fill={color} fontFamily="monospace" pointerEvents="none">RR</text>
    );
  },
  fracture: (finding, { crownTop, crownBottom, rootBase, rootPeak, color }) => {
    const subCode = finding.subCode || 'C_45';
    const isRoot = subCode.startsWith('R_');
    const angle = subCode.split('_')[1];

    const zoneTop = isRoot ? Math.min(rootBase, rootPeak) : crownTop;
    const zoneBottom = isRoot ? Math.max(rootBase, rootPeak) : crownBottom;
    const midY = (zoneTop + zoneBottom) / 2;

    if (angle === '90') {
      return (
        <line x1="5" y1={midY} x2="35" y2={midY}
          stroke={color} strokeWidth="2.5" pointerEvents="none" />
      );
    } else if (angle === '135') {
      return (
        <line x1="5" y1={zoneTop + 2} x2="35" y2={zoneBottom - 2}
          stroke={color} strokeWidth="2.5" pointerEvents="none" />
      );
    }
    return (
      <line x1="5" y1={zoneBottom - 2} x2="35" y2={zoneTop + 2}
        stroke={color} strokeWidth="2.5" pointerEvents="none" />
    );
  },
  extrudedTooth: (finding, { isUpper, crownBottom, crownTop, color }) => {
    const y1 = isUpper ? crownBottom + 3 : crownTop - 3;
    const y2 = isUpper ? crownBottom + 25 : crownTop - 25;
    const pts = isUpper ? `17,${y2 - 1} 20,${y2 + 6} 23,${y2 - 1}` : `17,${y2 + 1} 20,${y2 - 6} 23,${y2 + 1}`;
    return (
      <g stroke={color} strokeWidth="2" fill={color} pointerEvents="none">
        <line x1="20" y1={y1} x2="20" y2={y2} />
        <polygon points={pts} />
      </g>
    );
  },
  intrudedTooth: (finding, { isUpper, crownBottom, crownTop, color }) => {
    const y1 = isUpper ? crownBottom + 25 : crownTop - 25;
    const y2 = isUpper ? crownBottom + 3 : crownTop - 3;
    const pts = isUpper ? `17,${y2 + 6} 20,${y2 - 1} 23,${y2 + 6}` : `17,${y2 - 6} 20,${y2 + 1} 23,${y2 - 6}`;
    return (
      <g stroke={color} strokeWidth="2" fill={color} pointerEvents="none">
        <line x1="20" y1={y1} x2="20" y2={y2} />
        <polygon points={pts} />
      </g>
    );
  },
  pegTooth: (finding, { isUpper, numberY, color }) => {
    const halfBase = 13;
    const apexY = isUpper ? numberY - 10 : numberY + 6;
    const baseY = isUpper ? numberY + 2 : numberY - 6;
    return (
      <g pointerEvents="none">
        <polygon points={`20,${apexY} ${20 - halfBase},${baseY} ${20 + halfBase},${baseY}`} fill="none" stroke={color} strokeWidth="1.6" />
      </g>
    );
  },
  gemination: (finding, { numberY, color }) => (
    <g pointerEvents="none">
      <circle cx="20" cy={numberY - 2} r="10" fill="none" stroke={color} strokeWidth="1.6" />
    </g>
  ),
  gyroversion: (finding, { isUpper, crownBottom, crownTop, color }) => {
    const isClockwise = (finding.subCode || 'HOR') === 'HOR';
    const x1 = 6, x2 = 34, ctrlX = 20;
    const baseY = isUpper ? crownBottom + 8 : crownTop - 8;
    const arcHeight = isUpper ? 6 : -6;
    const arrowX = isClockwise ? x2 : x1;
    const tangentX = arrowX - ctrlX; const tangentY = baseY - (baseY + arcHeight);
    const length = Math.sqrt(tangentX * tangentX + tangentY * tangentY);
    const unitX = tangentX / length, unitY = tangentY / length; const perpX = -unitY, perpY = unitX;
    const armLen = 5, spread = 3;
    return (
      <g pointerEvents="none" stroke={color} strokeWidth="1.6" fill="none">
        <path d={`M ${x1},${baseY} Q ${ctrlX},${baseY + arcHeight} ${x2},${baseY}`} />
        <line x1={arrowX} y1={baseY} x2={arrowX - armLen * unitX + spread * perpX} y2={baseY - armLen * unitY + spread * perpY} />
        <line x1={arrowX} y1={baseY} x2={arrowX - armLen * unitX - spread * perpX} y2={baseY - armLen * unitY - spread * perpY} />
      </g>
    );
  },
  migration: (finding, { isUpper, crownBottom, crownTop, color }) => {
    const baseY = isUpper ? crownBottom + 8 : crownTop - 8;
    const pointsRight = (finding.subCode || 'DER') === 'DER';
    return (
      <g pointerEvents="none">
        <line x1={pointsRight ? "6" : "10"} y1={baseY} x2={pointsRight ? "30" : "34"} y2={baseY} stroke={color} strokeWidth="1.8" />
        <polygon points={pointsRight ? `34,${baseY} 28,${baseY - 4} 28,${baseY + 4}` : `6,${baseY} 12,${baseY - 4} 12,${baseY + 4}`} fill={color} />
      </g>
    );
  },
  definiteCrown: (finding, { crownTop, crownBottom, color }) => (
    <g pointerEvents="none">
      <rect x="0" y={crownTop - 1} width="40" height={crownBottom - crownTop + 2} fill="none" stroke={color} strokeWidth="2.5" rx="1" />
    </g>
  ),
  temporaryCrown: (finding, { crownTop, crownBottom, color }) => (
    <g pointerEvents="none">
      <rect x="0" y={crownTop - 1} width="40" height={crownBottom - crownTop + 2} fill="none" stroke={color} strokeWidth="2.5" rx="1" />
    </g>
  ),
  pulpTreatmentCompleted: (finding, { rootBase, rootPeak, color }) => (
    <g pointerEvents="none">
      <line x1="20" y1={rootBase} x2="20" y2={rootPeak + 4} stroke={color} strokeWidth="2.5" />
    </g>
  ),
  pulpTreatmentPending: (finding, { rootBase, rootPeak, color }) => (
    <g pointerEvents="none">
      <line x1="20" y1={rootBase} x2="20" y2={rootPeak + 4} stroke={color} strokeWidth="2.5" />
    </g>
  ),
  restoration: (finding, { innerPadding, crownBottom, crownTop, rootBase, rootPeak, isThirdMolar, isStandardMolar, isPremolar, isAnterior, isUpper, color }) => {
    if (!finding.surface) return null;
    const padding = innerPadding;

    if (finding.surface.startsWith('O')) {
      const parts = finding.surface.split('_'); const rowIndex = parseInt(parts[1], 10); const columnIndex = parseInt(parts[2], 10);
      let columns = 1, rows = 1;
      if (isThirdMolar) { columns = 3; rows = 2; } else if (isStandardMolar) { columns = 2; rows = 2; } else if (isPremolar) { columns = 1; rows = 2; }
      const cellWidth = (40 - 2 * padding) / columns; const cellHeight = (crownBottom - crownTop - 2 * padding) / rows;
      return <rect x={padding + columnIndex * cellWidth + 1} y={crownTop + padding + rowIndex * cellHeight + 1} width={cellWidth - 2} height={cellHeight - 2} fill="none" stroke={color} strokeWidth="2" pointerEvents="none" />;
    }

    if (finding.surface === 'A' || finding.surface.startsWith('A_')) {
      const isMolar = isStandardMolar || isThirdMolar;
      let rootPath = null;

      if (finding.surface === 'A') {
        rootPath = `M 0,${rootBase} L 20,${rootPeak} L 40,${rootBase} Z`;
      } else {
        const rootIdx = parseInt(finding.surface.split('_')[1], 10);
        if (isUpper && isMolar) {
          const peaks = [7, 20, 33];
          const bases = [[0, 14], [13, 27], [26, 40]];
          if (rootIdx < 3) {
            rootPath = `M ${bases[rootIdx][0]},${rootBase} L ${peaks[rootIdx]},${rootPeak} L ${bases[rootIdx][1]},${rootBase} Z`;
          }
        } else {
          const peaks = [10, 30];
          const bases = [[0, 20], [20, 40]];
          if (rootIdx < 2) {
            rootPath = `M ${bases[rootIdx][0]},${rootBase} L ${peaks[rootIdx]},${rootPeak} L ${bases[rootIdx][1]},${rootBase} Z`;
          }
        }
      }
      if (rootPath) return <path d={rootPath} fill="none" stroke={color} strokeWidth="2" pointerEvents="none" />;
      return null;
    }

    const midY = (crownTop + crownBottom) / 2;
    const paths = {
      'V': isAnterior ? `M 0,${crownTop} L 40,${crownTop} L 20,${midY} Z` : `M 0,${crownTop} L 40,${crownTop} L ${40 - padding},${crownTop + padding} L ${padding},${crownTop + padding} Z`,
      'L': isAnterior ? `M 0,${crownBottom} L 40,${crownBottom} L 20,${midY} Z` : `M 0,${crownBottom} L 40,${crownBottom} L ${40 - padding},${crownBottom - padding} L ${padding},${crownBottom - padding} Z`,
      'D': isAnterior ? `M 0,${crownTop} L 20,${midY} L 0,${crownBottom} Z` : `M 0,${crownTop} L ${padding},${crownTop + padding} L ${padding},${crownBottom - padding} L 0,${crownBottom} Z`,
      'M': isAnterior ? `M 40,${crownTop} L 20,${midY} L 40,${crownBottom} Z` : `M 40,${crownTop} L ${40 - padding},${crownTop + padding} L ${40 - padding},${crownBottom - padding} L 40,${crownBottom} Z`,
    };
    return <path d={paths[finding.surface]} fill="none" stroke={color} strokeWidth="2" pointerEvents="none" />;
  },
  temporaryRestoration: (finding, params) => conditionRenderers.restoration(finding, params),
  sealant: (finding, params) => conditionRenderers.restoration(finding, params),
  fixedOrthodonticAppliance: (finding, { crownTop, crownBottom, color }) => {
    const midY = (crownTop + crownBottom) / 2;
    return (
      <g stroke={color} strokeWidth="1.5" fill="none" pointerEvents="none">
        <rect x="10" y={midY - 5} width="20" height="10" rx="1" />
        <line x1="12" y1={midY} x2="28" y2={midY} />
        <line x1="20" y1={midY - 5} x2="20" y2={midY + 5} />
      </g>
    );
  },
  removableOrthodonticAppliance: (finding, { crownTop, crownBottom, color }) => {
    const midY = (crownTop + crownBottom) / 2;
    return (
      <path d={`M 5,${midY + 4} L 12,${midY - 4} L 20,${midY + 4} L 28,${midY - 4} L 35,${midY + 4}`} stroke={color} strokeWidth="2" fill="none" pointerEvents="none" />
    );
  },
};

export const gingivalInterpretation = (score) => {
  if (score === 0) return { label: 'Sano', color: '#52c41a', bg: '#f6ffed' };
  if (score <= 1.0) return { label: 'Gingivitis leve', color: '#faad14', bg: '#fffbe6' };
  if (score <= 2.0) return { label: 'Gingivitis moderada', color: '#fa8c16', bg: '#fff7e6' };
  if (score <= 3.0) return { label: 'Gingivitis severa', color: '#f5222d', bg: '#fff1f0' };
  return null;
};

export const codeByKey = {};
conditionCategories.forEach(category =>
  category.conditions.forEach(condition => {
    if (condition.symbolKey) codeByKey[condition.symbolKey] = condition.code;
  })
);

export const multiToothCodes = {
  diastema: 'DIASTEMA',
  supernumerary: 'SUPERNUMERARIO',
  transposition: 'TRANSPOSICION',
  fixedProsthesis: 'PROTESIS_FIJA',
  removableProsthesis: 'PROTESIS_REMOVIBLE',
  totalProsthesis: 'PROTESIS_TOTAL',
  totalEdentulous: 'EDENTULO_TOTAL',
  fixedOrthodontic: 'APARATO_ORTODONTICO_FIJO',
  removableOrthodontic: 'APARATO_ORTODONTICO_REMOVIBLE',
  fusion: 'FUSION',
};

export const dentalInputCodes = {
  dentalStatus: 'ESTADO_1_DENTAL',
  cpodC: 'CPOD_C_1_DENTAL',
  cpodP: 'CPOD_P_1_DENTAL',
  cpodO: 'CPOD_O_1_DENTAL',
  cpodTotal: 'CPOD_INDICE_1_DENTAL',
  softPlaque: 'PLACA_BLANDA_1_DENTAL',
  calcifiedPlaque: 'PLACA_CALCIFICADA_1_DENTAL',
  gingivalLegacy: 'GINGIVAL_1_DENTAL',
  gingivalValue: 'GINGIVAL_VALOR_1_DENTAL',
  gingivalInterpretation: 'GINGIVAL_INTERPRETACION_1_DENTAL',
  cariesCount: 'CONTEO_CARIES_1_DENTAL',
  missingCount: 'CONTEO_AUSENTES_1_DENTAL',
  remanentRootCount: 'CONTEO_REMANENTE_RADICULAR_1_DENTAL',
  necrosisCount: 'CONTEO_NECROSIS_1_DENTAL',
  badStateCount: 'CONTEO_MAL_ESTADO_1_DENTAL',
  cariesList: 'DIENTES_CARIES_1_DENTAL',
  missingList: 'DIENTES_AUSENTES_1_DENTAL',
  remanentRootList: 'DIENTES_REMANENTE_RADICULAR_1_DENTAL',
  necrosisList: 'DIENTES_NECROSIS_1_DENTAL',
  badStateList: 'DIENTES_MAL_ESTADO_1_DENTAL',
};

export const eraserCodes = {
  single: 'BORRADOR_SINGLE',
  multi: 'BORRADOR_MULTI',
  cpodExclude: 'CPOD_EXCLUIR',
};
